import React from 'react';
import {
    Box,
    TextField,
    Typography,
    Grid,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Controller, useFormContext } from 'react-hook-form';

// Keep in lockstep with CASE_ADDRESS_RELEVANCE in
// backend/assets/lambda/layers/livisibly_common/src/case_details.py
const relevanceOptions = [
    { value: 'marital_residence', label: 'Marital residence' },
    { value: 'post_separation_residence', label: 'Post-separation residence' },
    { value: 'prior_residence', label: 'Prior residence' },
    { value: 'vacation_property', label: 'Vacation property' },
    { value: 'other', label: 'Other' }
];

const AddressForm = ({ index, onRemove }) => {
    const { control, formState: { errors } } = useFormContext();

    const addressErrors = errors.addresses?.[index] || {};

    return (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Address #{index + 1}
                </Typography>
                <IconButton
                    type="button"
                    onClick={onRemove}
                    size="small"
                    color="error"
                    sx={{ '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Controller
                        name={`addresses.${index}.address`}
                        control={control}
                        rules={{
                            required: 'Address is required',
                            minLength: {
                                value: 4,
                                message: 'Enter a fuller address (street, city, state)'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Address"
                                placeholder="123 Main St, Atlanta, GA 30301"
                                error={!!addressErrors.address}
                                helperText={addressErrors.address?.message || 'Full address — geocoded on save'}
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`addresses.${index}.relevance`}
                        control={control}
                        rules={{ required: 'Select what this address is to the case' }}
                        render={({ field }) => (
                            <FormControl fullWidth size="small" error={!!addressErrors.relevance}>
                                <InputLabel>Relevance</InputLabel>
                                <Select {...field} label="Relevance">
                                    {relevanceOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {addressErrors.relevance && (
                                    <FormHelperText>{addressErrors.relevance.message}</FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`addresses.${index}.label`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Label"
                                error={!!addressErrors.label}
                                helperText="Optional (e.g. 'the beach house')"
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`addresses.${index}.effective_start`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Lived here from"
                                type="date"
                                error={!!addressErrors.effective_start}
                                helperText="Optional"
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`addresses.${index}.effective_end`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Lived here until"
                                type="date"
                                error={!!addressErrors.effective_end}
                                helperText="Optional"
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddressForm;
